import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import { useAuth } from "../AuthContext";
import { UserPlus, Trash2, Edit3, X, Check } from "lucide-react";

const ROLES = ["admin", "manager", "viewer"];
const roleStyle = {
  admin: "bg-red-100 text-red-700 border-red-300",
  manager: "bg-amber-100 text-amber-700 border-amber-300",
  viewer: "bg-emerald-100 text-emerald-700 border-emerald-300",
};

function CreateUserForm({ onCreated }) {
  const blank = { email: "", name: "", role: "viewer", password: "" };
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/admin/users", form);
      setForm(blank);
      onCreated();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Create failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="bg-white rounded-xl border border-slate-200 p-4 mb-5" data-testid="user-create-form">
      <div className="flex items-center gap-2 mb-3">
        <UserPlus size={18} style={{ color: "#00a3a3" }} />
        <h3 className="text-sm font-semibold" style={{ color: "#1a3666" }}>Add User</h3>
      </div>
      <div className="grid gap-3" style={{ gridTemplateColumns: "1.5fr 1.2fr 0.8fr 1fr auto" }}>
        <input required type="email" placeholder="email@example.com" value={form.email}
               onChange={(e) => setForm({ ...form, email: e.target.value })}
               className="px-3 py-2 border border-slate-200 rounded-md text-sm" data-testid="u-email" />
        <input required placeholder="Full name" value={form.name}
               onChange={(e) => setForm({ ...form, name: e.target.value })}
               className="px-3 py-2 border border-slate-200 rounded-md text-sm" data-testid="u-name" />
        <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="px-3 py-2 border border-slate-200 rounded-md text-sm" data-testid="u-role">
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <input required type="password" placeholder="Password (≥6 chars)" value={form.password}
               onChange={(e) => setForm({ ...form, password: e.target.value })}
               minLength={6}
               className="px-3 py-2 border border-slate-200 rounded-md text-sm" data-testid="u-password" />
        <button type="submit" disabled={busy} data-testid="u-create"
                className="px-4 py-2 rounded-md text-white text-sm font-semibold disabled:opacity-60"
                style={{ background: "#1a3666" }}>
          {busy ? "Adding…" : "Add"}
        </button>
      </div>
      {err && <div className="text-red-600 text-xs mt-2" data-testid="u-create-error">{err}</div>}
    </form>
  );
}

function UserRow({ u, meId, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(u.name);
  const [role, setRole] = useState(u.role);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const isSelf = u.id === meId;

  async function save() {
    setBusy(true);
    const patch = {};
    if (name !== u.name) patch.name = name;
    if (role !== u.role) patch.role = role;
    if (password) patch.password = password;
    if (!Object.keys(patch).length) { setEditing(false); setBusy(false); return; }
    try {
      await api.patch(`/admin/users/${u.id}`, patch);
      setPassword("");
      setEditing(false);
      onChanged();
    } catch (e) {
      alert(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  }

  async function del() {
    if (!confirm(`Delete ${u.email}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/users/${u.id}`);
      onChanged();
    } catch (e) {
      alert(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  }

  if (!editing) {
    return (
      <tr className="border-t border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.email}`}>
        <td className="px-3 py-2 font-medium">{u.email}{isSelf && <span className="ml-2 text-[10px] text-slate-400">(you)</span>}</td>
        <td className="px-3 py-2">{u.name}</td>
        <td className="px-3 py-2">
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${roleStyle[u.role]}`}>{u.role}</span>
        </td>
        <td className="px-3 py-2 text-xs text-slate-500">{u.last_login ? new Date(u.last_login).toLocaleString() : "—"}</td>
        <td className="px-3 py-2 text-center space-x-3">
          <button onClick={() => setEditing(true)} className="text-blue-600 hover:underline text-xs inline-flex items-center gap-1"
                  data-testid={`edit-${u.email}`}><Edit3 size={12} /> Edit</button>
          {!isSelf && (
            <button onClick={del} disabled={busy} className="text-red-600 hover:underline text-xs inline-flex items-center gap-1"
                    data-testid={`delete-${u.email}`}><Trash2 size={12} /> Delete</button>
          )}
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-t border-slate-100 bg-blue-50">
      <td className="px-3 py-2 text-slate-600">{u.email}</td>
      <td className="px-3 py-2">
        <input value={name} onChange={(e) => setName(e.target.value)}
               className="w-full px-2 py-1 border rounded text-sm" />
      </td>
      <td className="px-3 py-2">
        <select value={role} onChange={(e) => setRole(e.target.value)}
                className="w-full px-2 py-1 border rounded text-sm">
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </td>
      <td className="px-3 py-2">
        <input type="password" placeholder="New password (optional)" value={password}
               onChange={(e) => setPassword(e.target.value)}
               className="w-full px-2 py-1 border rounded text-sm" />
      </td>
      <td className="px-3 py-2 text-center space-x-2">
        <button onClick={save} disabled={busy} className="text-emerald-600 hover:underline text-xs inline-flex items-center gap-1"
                data-testid={`save-${u.email}`}><Check size={12} /> Save</button>
        <button onClick={() => { setEditing(false); setName(u.name); setRole(u.role); setPassword(""); }}
                className="text-slate-500 hover:underline text-xs inline-flex items-center gap-1"><X size={12} /> Cancel</button>
      </td>
    </tr>
  );
}

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data.users || []);
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Load failed");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (err) return <div className="text-red-600" data-testid="users-error">{err}</div>;

  return (
    <div data-testid="users-page">
      <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold" style={{ color: "#1a3666" }}>
            User Management <span className="text-xs font-normal bg-red-100 text-red-700 px-2 py-0.5 rounded-full ml-2">Admin</span>
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">{users.length} user{users.length === 1 ? "" : "s"}</p>
        </div>
      </div>

      <CreateUserForm onCreated={load} />

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase">
            <tr>
              <th className="px-3 py-2 text-left">Email</th>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">Role</th>
              <th className="px-3 py-2 text-left">Last Login</th>
              <th className="px-3 py-2 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => <UserRow key={u.id} u={u} meId={user?.id} onChanged={load} />)}
            {!users.length && <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400">No users yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
